import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal } from '../../../../components';
import { generateMailboxPassword } from '../../password';
import { validateMailboxPassword } from '../../state';
import { PasswordEntry } from '../PasswordEntry';
import { Body, Line } from './RotatePasswordDialog.styles';

export type RotatePasswordDialogProps = {
  open: boolean;
  /** The mailbox whose password would be rotated. Kept while the dialog animates closed. */
  address: string;
  onCancel: () => void;
  /** Confirmed: the caller rotates to `password` (valid by the service's bounds). */
  onConfirm: (password: string) => void;
};

/**
 * Changing a mailbox's password: ONE confirmation, used by the mailbox page's Change password and by
 * the create form's offer after a `409`, so the warning cannot diverge between them.
 *
 * The operator chooses the new password here, as on the create form: the same password row,
 * PREFILLED with a generated one (the one generator, `password.ts`), masked, the eye leading, copy
 * after the value and Regenerate at the far right, editable, with the strength meter once they type
 * their own. Change password is disabled while the value is outside the service's bounds, and passes the value
 * on; the provider's own refusal comes back through the caller. After a `409` the same field reads
 * as choosing a password for the mailbox that already existed, which is what it is. Each opening
 * starts from a fresh generated value.
 *
 * The warning is the point, and it is confirmed, not assumed: there are no per-device passwords, so
 * a change signs the owner out of EVERY mail client at once until the new password is entered on
 * each. An operator who changes it without knowing that has just broken someone's mail on every
 * device. The title is just "Change password"; the address sits under it as one muted line. The
 * shared Modal owns Escape, the backdrop and focus return. No "Shown once" hint on the field: the
 * second warning sentence already says it.
 */
export const RotatePasswordDialog = ({
  open,
  address,
  onCancel,
  onConfirm,
}: RotatePasswordDialogProps): React.ReactElement => {
  const { t } = useTranslation();
  const [password, setPassword] = React.useState(generateMailboxPassword);
  // Typed by the operator (their own): the meter shows. A generated value is not an edit.
  const [edited, setEdited] = React.useState(false);
  // A fresh generated value on every opening, adjusted while rendering (no effect needed).
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPassword(generateMailboxPassword());
      setEdited(false);
    }
  }
  const valid = validateMailboxPassword(password) === null;
  const onChange = React.useCallback((value: string) => {
    setPassword(value);
    setEdited(true);
  }, []);
  const onRegenerate = React.useCallback(() => {
    setPassword(generateMailboxPassword());
    setEdited(false);
  }, []);
  // The gate lives in the handler as well as on the button.
  const confirm = React.useCallback(() => {
    if (valid) onConfirm(password);
  }, [valid, onConfirm, password]);

  return (
    <Modal
      open={open}
      title={t('Change password')}
      onClose={onCancel}
      footer={
        <>
          <Button label={t('Cancel')} variant="outline" onClick={onCancel} />
          <Button label={t('Change password')} variant="primary" onClick={confirm} disabled={!valid} />
        </>
      }
    >
      <Body data-testid="mailbox-rotate-dialog">
        {/* The address, one muted line under the title: needed, because the same dialog serves the
            create form's 409 offer, where the mailbox is not the one the operator just opened. */}
        <Line text={address} tag="span" raw />
        <PasswordEntry
          label={t('Password')}
          value={password}
          onChange={onChange}
          onRegenerate={onRegenerate}
          copyLabel={t('Copy password')}
          edited={edited}
        />
        {/* Both facts, in two sentences: every client is signed out, and the password is not
            recoverable. "Change password" sounds gentler than "Rotate", so this warning carries more
            weight, not less: do not soften it. */}
        <Line text="Every device signed in to this mailbox stops working until you enter the new password." tag="span" />
        <Line text="It is shown once and stored nowhere." tag="span" />
      </Body>
    </Modal>
  );
};

export default RotatePasswordDialog;
